"use client";

import {
  createContext,
  useContext,
  useSyncExternalStore,
  type ReactNode,
} from "react";

const MOBILE_BREAKPOINT = 768;

const listeners = new Set<() => void>();
let isMobileSnapshot: boolean | undefined;
let mediaWatcherStarted = false;

function setSnapshot(next: boolean) {
  if (isMobileSnapshot === next) return;
  isMobileSnapshot = next;
  for (const listener of listeners) listener();
}

function startMediaWatcher() {
  mediaWatcherStarted = true;

  if (typeof window.matchMedia !== "function") {
    const updateFromWidth = () => {
      setSnapshot(window.innerWidth < MOBILE_BREAKPOINT);
    };
    updateFromWidth();
    window.addEventListener("resize", updateFromWidth, { passive: true });
    return;
  }

  const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);

  const update = (event?: MediaQueryListEvent) => {
    const next =
      typeof event?.matches === "boolean"
        ? event.matches
        : typeof mql.matches === "boolean"
          ? mql.matches
          : window.innerWidth < MOBILE_BREAKPOINT;

    setSnapshot(next);
  };

  update();

  if (typeof mql.addEventListener === "function") {
    mql.addEventListener("change", update);
  } else {
    mql.addListener(update);
  }
}

function subscribe(listener: () => void) {
  if (!mediaWatcherStarted && typeof window !== "undefined") {
    startMediaWatcher();
  }
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): boolean {
  if (isMobileSnapshot === undefined) {
    isMobileSnapshot = readMaxWidth(MOBILE_BREAKPOINT);
  }
  return isMobileSnapshot;
}
const getMobileServerSnapshot = () => true;
const getDesktopServerSnapshot = () => false;

const MobileHintContext = createContext(false);

export function MobileHintProvider({ value, children }: { value: boolean; children: ReactNode }) {
  return <MobileHintContext.Provider value={value}>{children}</MobileHintContext.Provider>;
}

export function useMobileHint(): boolean {
  return useContext(MobileHintContext);
}

export function useIsMobile(): boolean {
  const hint = useMobileHint();
  return useSyncExternalStore(
    subscribe,
    getSnapshot,
    hint ? getMobileServerSnapshot : getDesktopServerSnapshot,
  );
}

function readMaxWidth(maxWidth: number): boolean {
  if (typeof window.matchMedia === "function") {
    return window.matchMedia(`(max-width: ${maxWidth - 1}px)`).matches;
  }
  return window.innerWidth < maxWidth;
}
